import { state } from '../state.js';
import { CategoryTreeNode, DiffPlan, ValidationScanResult } from '../types.js';
import { escapeHtml, formatRelativeTime } from '../utils/formatters.js';
import { showToast } from '../utils/toast.js';
import { openJobDetailModal } from './logs.controller.js';

export const ENTITY_TYPE_METADATA: Record<string, { name: string; desc: string; icon: string; deps: string[] }> = {
  system_config: {
    name: 'System Config',
    desc: 'System settings, measure families, & configurations',
    icon: '⚙️',
    deps: [],
  },
  locales: {
    name: 'Locales',
    desc: 'Supported language and regional locales',
    icon: '🌐',
    deps: [],
  },
  currencies: {
    name: 'Currencies',
    desc: 'Active system currencies and monetary symbols',
    icon: '💱',
    deps: [],
  },
  association_types: {
    name: 'Association Types',
    desc: 'Cross-sell, up-sell, and substitution links',
    icon: '🔗',
    deps: [],
  },
  categories: {
    name: 'Category Tree',
    desc: 'Hierarchical taxonomy nodes and position trees',
    icon: '🌲',
    deps: [],
  },
  attribute_groups: {
    name: 'Attribute Groups',
    desc: 'Classification groups for attribute sets',
    icon: '📑',
    deps: [],
  },
  attributes: {
    name: 'Attributes',
    desc: 'Attribute data types, validation rules, and scopes',
    icon: '🏷️',
    deps: ['attribute_groups'],
  },
  attribute_options: {
    name: 'Attribute Options',
    desc: 'Option select values and localized labels',
    icon: '🔘',
    deps: ['attributes'],
  },
  families: {
    name: 'Families',
    desc: 'Family attribute requirements and channels',
    icon: '👔',
    deps: ['attributes', 'attribute_groups'],
  },
  family_variants: {
    name: 'Family Variants',
    desc: 'Variant axes, levels, and variant attribute sets',
    icon: '🧬',
    deps: ['families', 'attributes'],
  },
  product_models: {
    name: 'Product Models',
    desc: 'Two-level parent product models and variations',
    icon: '📐',
    deps: ['families', 'categories', 'attributes'],
  },
  products: {
    name: 'Products',
    desc: 'Concrete product SKU records, values, & media',
    icon: '👟',
    deps: ['families', 'categories', 'attributes', 'product_models'],
  },
  asset_families: {
    name: 'Asset Families',
    desc: 'Asset manager families and transformation rules',
    icon: '🖼️',
    deps: ['attributes'],
  },
  assets: {
    name: 'Assets',
    desc: 'Digital asset media records and binary files',
    icon: '📁',
    deps: ['asset_families'],
  },
};

/**
 * Fetch initial dataset for Batch Sync & Catalog Browser
 */
export async function fetchBatchInitialData(): Promise<void> {
  await Promise.allSettled([
    fetchDiscoverySummary(),
    fetchCategoryTree(),
    fetchCatalogFilters(),
    fetchCatalogItems(),
    checkBatchReadiness(),
  ]);
  renderEntityTypesGrid();
  updateTopologicalOrder();
}

/**
 * Switch sub-tabs inside Batch view
 */
export function switchBatchSubTab(subTabId: 'catalog' | 'orchestrator' | 'diffs'): void {
  const validSubTabs = ['catalog', 'orchestrator', 'diffs'];
  if (!validSubTabs.includes(subTabId)) subTabId = 'catalog';

  state.batch.activeSubTab = subTabId;

  // Subtab buttons
  document.querySelectorAll('#batch-view-container .subtab-btn').forEach((btn) => {
    const isTarget = btn.getAttribute('data-subtab') === subTabId;
    btn.classList.toggle('active', isTarget);
    btn.setAttribute('aria-selected', isTarget ? 'true' : 'false');
  });

  // Subtab panels
  const catalogView = document.getElementById('batch-catalog-view');
  const orchView = document.getElementById('batch-orchestrator-view');
  const diffsView = document.getElementById('batch-diffs-view');

  if (catalogView) catalogView.style.display = subTabId === 'catalog' ? 'block' : 'none';
  if (orchView) orchView.style.display = subTabId === 'orchestrator' ? 'block' : 'none';
  if (diffsView) diffsView.style.display = subTabId === 'diffs' ? 'block' : 'none';

  if (subTabId === 'orchestrator') {
    renderEntityTypesGrid();
    updateTopologicalOrder();
  }
}

/**
 * Fetch Discovery summary counts
 */
export async function fetchDiscoverySummary(): Promise<void> {
  try {
    const res = await fetch('/api/v1/discovery/summary');
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    state.batch.summary = data.summary || data.counts || {};

    // Total entities sum
    const totalCount = Object.values(state.batch.summary).reduce((acc: number, val: any) => acc + (typeof val === 'number' ? val : (val.count || 0)), 0);
    const metricTotal = document.getElementById('metric-batch-total-entities');
    if (metricTotal) {
      metricTotal.textContent = totalCount > 0 ? totalCount.toLocaleString() : '--';
    }

    const badgeProducts = document.getElementById('catalog-badge-products-count');
    if (badgeProducts && state.batch.summary.products) {
      const pCount = typeof state.batch.summary.products === 'number' ? state.batch.summary.products : (state.batch.summary.products.count || 0);
      badgeProducts.textContent = pCount.toLocaleString();
    }

    const badgeModels = document.getElementById('catalog-badge-models-count');
    if (badgeModels && state.batch.summary.product_models) {
      const mCount = typeof state.batch.summary.product_models === 'number' ? state.batch.summary.product_models : (state.batch.summary.product_models.count || 0);
      badgeModels.textContent = mCount.toLocaleString();
    }
  } catch (err) {
    console.warn('Could not fetch discovery summary:', err);
  }
}

/**
 * Fetch hierarchical category tree
 */
export async function fetchCategoryTree(): Promise<void> {
  const container = document.getElementById('category-tree-container');
  try {
    const res = await fetch('/api/v1/discovery/categories/tree?with_position=true');
    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      const err: any = new Error(errData.message || `HTTP ${res.status}`);
      err.status = res.status;
      err.data = errData;
      throw err;
    }
    const data = await res.json();
    state.batch.catalog.categoryTree = data.tree || [];

    const totalBadge = document.getElementById('tree-total-count');
    if (totalBadge) totalBadge.textContent = String(countTreeNodes(state.batch.catalog.categoryTree));

    renderCategoryTree();
  } catch (err: any) {
    if (container) {
      const is403 =
        err.status === 403 ||
        err.data?.code === 'akeneo_permission_denied' ||
        err.data?.error === 'akeneo_permission_denied' ||
        String(err.message).includes('403');
      if (is403) {
        container.innerHTML = `
          <div class="permission-warning-card alert alert-warning" style="padding: 1rem; margin: 0.5rem; border-radius: 6px; font-size: 0.85rem;">
            <div style="font-weight: 600; margin-bottom: 0.25rem;">⚠️ Akeneo 403 Forbidden: Permission Denied</div>
            <div>The API user/role requires <strong>Category Tree access (Read/Write)</strong>, <strong>Product View</strong>, <strong>Locale access</strong>, and <strong>Event Platform permissions</strong>.</div>
          </div>
        `;
      } else {
        container.innerHTML = `<div class="tree-loading-state text-warning">Categories not loaded: ${escapeHtml(err.message)}</div>`;
      }
    }
  }
}

export function countTreeNodes(nodes: CategoryTreeNode[]): number {
  let count = 0;
  function traverse(list: CategoryTreeNode[]) {
    if (!Array.isArray(list)) return;
    count += list.length;
    list.forEach((n) => {
      if (n.children && n.children.length) traverse(n.children);
    });
  }
  traverse(nodes);
  return count;
}

/**
 * Render hierarchical category tree nodes
 */
export function renderCategoryTree(): void {
  const container = document.getElementById('category-tree-container');
  if (!container) return;

  const tree = state.batch.catalog.categoryTree;
  const search = state.batch.catalog.categorySearch.trim().toLowerCase();

  if (!tree || tree.length === 0) {
    container.innerHTML = '<div class="tree-loading-state">No categories discovered in source.</div>';
    return;
  }

  function renderNodes(nodes: CategoryTreeNode[]): string {
    let html = '';
    nodes.forEach((node) => {
      if (search && !filterCategoryTreeNode(node, search)) {
        return;
      }

      const hasChildren = Array.isArray(node.children) && node.children.length > 0;
      const isSelected = state.batch.catalog.selectedCategory === node.code;
      const isExpanded = state.batch.catalog.expandedNodes.has(node.code) || search.length > 0;
      const label = node.labels?.en_US || (node.labels && Object.values(node.labels)[0]) || node.code;

      html += `
        <div class="tree-node-item" data-code="${escapeHtml(node.code)}">
          <div class="tree-node-row ${isSelected ? 'selected' : ''}" data-code="${escapeHtml(node.code)}">
            ${
              hasChildren
                ? `<button type="button" class="tree-expander-btn ${isExpanded ? 'expanded' : ''}" data-toggle-code="${escapeHtml(node.code)}" aria-label="Toggle ${escapeHtml(node.code)}">▶</button>`
                : '<span style="width: 18px; display: inline-block;"></span>'
            }
            <span class="tree-node-icon">📁</span>
            <span class="tree-node-title" title="${escapeHtml(label)} (${escapeHtml(node.code)})">${escapeHtml(label)}</span>
            <span class="dep-pill">${escapeHtml(node.code)}</span>
          </div>
          ${
            hasChildren
              ? `<div class="tree-children-container" style="display: ${isExpanded ? 'block' : 'none'};">
                  ${renderNodes(node.children!)}
                </div>`
              : ''
          }
        </div>
      `;
    });
    return html;
  }

  container.innerHTML = renderNodes(tree);

  // Attach tree event listeners
  container.querySelectorAll('.tree-node-row').forEach((row) => {
    row.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).closest('.tree-expander-btn')) return;
      const code = row.getAttribute('data-code');
      if (code) selectCategoryFilter(code);
    });
  });

  container.querySelectorAll('.tree-expander-btn').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const code = btn.getAttribute('data-toggle-code');
      if (!code) return;
      if (state.batch.catalog.expandedNodes.has(code)) {
        state.batch.catalog.expandedNodes.delete(code);
      } else {
        state.batch.catalog.expandedNodes.add(code);
      }
      renderCategoryTree();
    });
  });
}

export function filterCategoryTreeNode(node: CategoryTreeNode, query: string): boolean {
  const label = node.labels?.en_US || (node.labels && Object.values(node.labels)[0]) || '';
  if (node.code.toLowerCase().includes(query) || label.toLowerCase().includes(query)) {
    return true;
  }
  if (Array.isArray(node.children)) {
    return node.children.some((child) => filterCategoryTreeNode(child, query));
  }
  return false;
}

/**
 * Set or clear category filter
 */
export function selectCategoryFilter(categoryCode: string | null): void {
  state.batch.catalog.selectedCategory = categoryCode;
  state.batch.catalog.page = 1;

  const rootBtn = document.getElementById('tree-clear-selection');
  if (rootBtn) {
    rootBtn.classList.toggle('active', !categoryCode);
  }

  renderCategoryTree();
  fetchCatalogItems();
}

/**
 * Fetch families and channels for filter dropdowns
 */
export async function fetchCatalogFilters(): Promise<void> {
  try {
    const [famRes, chanRes] = await Promise.allSettled([
      fetch('/api/v1/discovery/families?limit=100'),
      fetch('/api/v1/discovery/channels?limit=100'),
    ]);

    if (famRes.status === 'fulfilled' && famRes.value.ok) {
      const famData = await famRes.value.json();
      state.batch.catalog.families = famData.items || [];
      const famSelect = document.getElementById('catalog-family-filter') as HTMLSelectElement | null;
      if (famSelect) {
        famSelect.innerHTML = '<option value="">All Families</option>' +
          state.batch.catalog.families.map((f) => `<option value="${escapeHtml(f.code)}">${escapeHtml(f.labels?.en_US || f.code)}</option>`).join('');
      }
    }

    if (chanRes.status === 'fulfilled' && chanRes.value.ok) {
      const chanData = await chanRes.value.json();
      state.batch.catalog.channels = chanData.items || [];
      const chanSelect = document.getElementById('catalog-channel-filter') as HTMLSelectElement | null;
      if (chanSelect) {
        chanSelect.innerHTML = '<option value="">All Channels</option>' +
          state.batch.catalog.channels.map((c) => `<option value="${escapeHtml(c.code)}">${escapeHtml(c.labels?.en_US || c.code)}</option>`).join('');
      }
    }
  } catch (err) {
    console.warn('Could not fetch catalog filter options:', err);
  }
}

/**
 * Fetch paginated products or product-models
 */
export async function fetchCatalogItems(): Promise<void> {
  const tableBody = document.getElementById('catalog-table-body');
  const isProducts = state.batch.catalog.entityType === 'products';

  state.batch.catalog.loading = true;

  if (tableBody) {
    tableBody.innerHTML = `
      <tr>
        <td colspan="7" style="text-align: center; padding: 2rem; color: var(--text-secondary);">
          <span class="spinner-sm"></span> Loading ${isProducts ? 'products' : 'product models'} from source...
        </td>
      </tr>
    `;
  }

  const endpoint = isProducts ? '/api/v1/discovery/products' : '/api/v1/discovery/product-models';
  const params = new URLSearchParams();
  params.set('page', String(state.batch.catalog.page));
  params.set('limit', String(state.batch.catalog.limit));

  if (state.batch.catalog.selectedCategory) {
    params.set('categories', state.batch.catalog.selectedCategory);
  }
  const familyFilter = (document.getElementById('catalog-family-filter') as HTMLSelectElement | null)?.value;
  if (familyFilter) params.set('family', familyFilter);

  const channelFilter = (document.getElementById('catalog-channel-filter') as HTMLSelectElement | null)?.value;
  if (channelFilter) params.set('scope', channelFilter);

  const statusFilter = (document.getElementById('catalog-status-filter') as HTMLSelectElement | null)?.value;
  if (statusFilter !== '' && statusFilter !== undefined && statusFilter !== null) params.set('enabled', statusFilter);

  const compFilter = (document.getElementById('catalog-completeness-filter') as HTMLSelectElement | null)?.value;
  if (compFilter) params.set('completeness', compFilter);

  const updatedFilter = (document.getElementById('catalog-updated-filter') as HTMLInputElement | null)?.value;
  if (updatedFilter) params.set('updated_since', new Date(updatedFilter).toISOString());

  const searchInput = (document.getElementById('catalog-search-input') as HTMLInputElement | null)?.value.trim();
  if (searchInput) params.set('search', searchInput);

  try {
    const res = await fetch(`${endpoint}?${params.toString()}`);
    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      const err: any = new Error(errData.message || `HTTP ${res.status}`);
      err.status = res.status;
      err.data = errData;
      throw err;
    }
    const data = await res.json();

    state.batch.catalog.items = data.items || [];
    state.batch.catalog.totalCount = data.totalCount !== undefined ? data.totalCount : state.batch.catalog.items.length;
    state.batch.catalog.hasMore = Boolean(data.hasMore);

    renderCatalogTable();
    renderCatalogPagination();
    renderActiveFilterChips();
  } catch (err: any) {
    if (tableBody) {
      const is403 =
        err.status === 403 ||
        err.data?.code === 'akeneo_permission_denied' ||
        err.data?.error === 'akeneo_permission_denied' ||
        String(err.message).includes('403');
      if (is403) {
        tableBody.innerHTML = `
          <tr>
            <td colspan="7" style="padding: 1.5rem;">
              <div class="permission-warning-card alert alert-warning" style="background: rgba(245, 158, 11, 0.1); border: 1px solid var(--status-warning, #f59e0b); border-radius: 8px; padding: 1.25rem;">
                <div style="display: flex; gap: 0.75rem; align-items: flex-start;">
                  <span style="font-size: 1.5rem; line-height: 1;">⚠️</span>
                  <div>
                    <h4 style="margin: 0 0 0.5rem 0; color: var(--text-primary); font-weight: 600;">Akeneo 403 Forbidden: Missing API Permissions</h4>
                    <p style="margin: 0 0 0.5rem 0; color: var(--text-secondary); font-size: 0.9rem;">
                      The Akeneo API user/role requires <strong>Product View</strong>, <strong>Category Tree access (Read/Write)</strong>, <strong>Locale access</strong>, and <strong>Event Platform permissions</strong>.
                    </p>
                    <p style="margin: 0; font-size: 0.8rem; color: var(--text-secondary);">
                      Ensure the API user/role has Product View permissions in Akeneo PIM under <em>System &gt; Roles / Users</em>.
                    </p>
                  </div>
                </div>
              </div>
            </td>
          </tr>
        `;
      } else {
        tableBody.innerHTML = `
          <tr>
            <td colspan="7" style="text-align: center; padding: 2rem; color: var(--status-danger);">
              Failed to load items: ${escapeHtml(err.message)}
            </td>
          </tr>
        `;
      }
    }
  } finally {
    state.batch.catalog.loading = false;
  }
}

/**
 * Render catalog items data table
 */
export function renderCatalogTable(): void {
  const tableBody = document.getElementById('catalog-table-body');
  const emptyState = document.getElementById('catalog-empty-state');
  const selectAllCb = document.getElementById('catalog-select-all-cb') as HTMLInputElement | null;
  if (!tableBody) return;

  const items = state.batch.catalog.items;
  const isProducts = state.batch.catalog.entityType === 'products';

  if (!items || items.length === 0) {
    tableBody.innerHTML = '';
    if (emptyState) emptyState.style.display = 'block';
    if (selectAllCb) selectAllCb.checked = false;
    updateSelectionBar();
    return;
  }

  if (emptyState) emptyState.style.display = 'none';

  let allPageSelected = true;

  const rowsHtml = items.map((item) => {
    const id = item.identifier || item.code || '';
    const isSelected = state.batch.catalog.selectedItemIds.has(id);
    if (!isSelected) allPageSelected = false;

    const family = item.family || 'None';
    const categories = Array.isArray(item.categories) && item.categories.length > 0 ? item.categories : ['None'];
    const isEnabled = item.enabled !== false;
    const updatedAt = item.updated ? formatRelativeTime(item.updated) : 'N/A';
    const icon = isProducts ? '👟' : '📐';

    return `
      <tr class="${isSelected ? 'row-selected' : ''}" data-id="${escapeHtml(id)}">
        <td style="text-align: center;">
          <input type="checkbox" class="form-checkbox catalog-item-cb" data-id="${escapeHtml(id)}" ${isSelected ? 'checked' : ''} />
        </td>
        <td>
          <div style="display: flex; align-items: center; gap: 0.5rem;">
            <span>${icon}</span>
            <strong class="mono">${escapeHtml(id)}</strong>
          </div>
        </td>
        <td>
          <span class="badge badge-neutral">${escapeHtml(family)}</span>
        </td>
        <td>
          <div style="display: flex; gap: 0.25rem; flex-wrap: wrap;">
            ${categories.slice(0, 3).map((c) => `<span class="dep-pill">${escapeHtml(c)}</span>`).join('')}
            ${categories.length > 3 ? `<span class="dep-pill">+${categories.length - 3}</span>` : ''}
          </div>
        </td>
        <td>
          <div style="display: flex; align-items: center; gap: 0.4rem;">
            <span class="status-badge-inline ${isEnabled ? 'status-completed' : 'status-failed'}">
              ${isEnabled ? 'Enabled' : 'Disabled'}
            </span>
          </div>
        </td>
        <td class="text-secondary" style="font-size: 0.8rem;">
          ${escapeHtml(updatedAt)}
        </td>
        <td style="text-align: right;">
          <div style="display: inline-flex; gap: 0.35rem;">
            <button type="button" class="btn btn-xs btn-outline btn-inspect-item" data-id="${escapeHtml(id)}" title="Inspect Raw JSON Details">Inspect</button>
            <button type="button" class="btn btn-xs btn-secondary btn-dryrun-item" data-id="${escapeHtml(id)}" title="Dry-Run Diff Simulation">Dry-Run</button>
            <button type="button" class="btn btn-xs btn-primary btn-sync-item" data-id="${escapeHtml(id)}" title="Selective Live Sync">Sync</button>
          </div>
        </td>
      </tr>
    `;
  }).join('');

  tableBody.innerHTML = rowsHtml;

  if (selectAllCb) {
    selectAllCb.checked = allPageSelected && items.length > 0;
  }

  // Attach row event listeners
  tableBody.querySelectorAll('.catalog-item-cb').forEach((cb) => {
    cb.addEventListener('change', () => {
      const id = cb.getAttribute('data-id');
      if (id) toggleCatalogSelection(id, (cb as HTMLInputElement).checked);
    });
  });

  tableBody.querySelectorAll('.btn-inspect-item').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.getAttribute('data-id');
      if (id) inspectCatalogEntity(state.batch.catalog.entityType, id);
    });
  });

  tableBody.querySelectorAll('.btn-dryrun-item').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.getAttribute('data-id');
      if (id) {
        state.batch.catalog.selectedItemIds.add(id);
        executeSelectiveDryRun();
      }
    });
  });

  tableBody.querySelectorAll('.btn-sync-item').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.getAttribute('data-id');
      if (id) {
        state.batch.catalog.selectedItemIds.add(id);
        executeSelectiveSync();
      }
    });
  });

  updateSelectionBar();
}

/**
 * Render pagination controls for catalog
 */
export function renderCatalogPagination(): void {
  const infoEl = document.getElementById('catalog-pagination-info');
  const prevBtn = document.getElementById('catalog-btn-prev') as HTMLButtonElement | null;
  const nextBtn = document.getElementById('catalog-btn-next') as HTMLButtonElement | null;
  const pagesContainer = document.getElementById('catalog-page-numbers');

  const { page, limit, totalCount } = state.batch.catalog;
  const totalPages = Math.max(1, Math.ceil(totalCount / limit));

  const start = totalCount === 0 ? 0 : (page - 1) * limit + 1;
  const end = Math.min(page * limit, totalCount);

  if (infoEl) {
    infoEl.textContent = `Showing ${start} - ${end} of ${totalCount} items`;
  }

  if (prevBtn) prevBtn.disabled = page <= 1;
  if (nextBtn) nextBtn.disabled = page >= totalPages;

  if (pagesContainer) {
    let pagesHtml = '';
    const maxVisiblePages = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let p = startPage; p <= endPage; p++) {
      pagesHtml += `<button type="button" class="pagination-page-btn ${p === page ? 'active' : ''}" data-page="${p}">${p}</button>`;
    }
    pagesContainer.innerHTML = pagesHtml;

    pagesContainer.querySelectorAll('.pagination-page-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const p = Number(btn.getAttribute('data-page'));
        if (p && p !== state.batch.catalog.page) {
          state.batch.catalog.page = p;
          fetchCatalogItems();
        }
      });
    });
  }
}

/**
 * Render active filter tags/chips
 */
export function renderActiveFilterChips(): void {
  const container = document.getElementById('catalog-active-filters-chips');
  const list = document.getElementById('filter-chips-list');
  if (!container || !list) return;

  const chips: Array<{ label: string; key: string }> = [];
  if (state.batch.catalog.selectedCategory) {
    chips.push({ label: `Category: ${state.batch.catalog.selectedCategory}`, key: 'category' });
  }
  const fam = (document.getElementById('catalog-family-filter') as HTMLSelectElement | null)?.value;
  if (fam) chips.push({ label: `Family: ${fam}`, key: 'family' });

  const chan = (document.getElementById('catalog-channel-filter') as HTMLSelectElement | null)?.value;
  if (chan) chips.push({ label: `Channel: ${chan}`, key: 'channel' });

  const search = (document.getElementById('catalog-search-input') as HTMLInputElement | null)?.value.trim();
  if (search) chips.push({ label: `Search: "${search}"`, key: 'search' });

  if (chips.length === 0) {
    container.style.display = 'none';
    list.innerHTML = '';
    return;
  }

  container.style.display = 'flex';
  list.innerHTML = chips.map((c) => `
    <span class="filter-chip">
      ${escapeHtml(c.label)}
      <span class="filter-chip-remove" data-key="${c.key}">✕</span>
    </span>
  `).join('');

  list.querySelectorAll('.filter-chip-remove').forEach((btn) => {
    btn.addEventListener('click', () => {
      const key = btn.getAttribute('data-key');
      if (key === 'category') selectCategoryFilter(null);
      if (key === 'family') {
        const el = document.getElementById('catalog-family-filter') as HTMLSelectElement | null;
        if (el) el.value = '';
        fetchCatalogItems();
      }
      if (key === 'channel') {
        const el = document.getElementById('catalog-channel-filter') as HTMLSelectElement | null;
        if (el) el.value = '';
        fetchCatalogItems();
      }
      if (key === 'search') {
        const el = document.getElementById('catalog-search-input') as HTMLInputElement | null;
        if (el) el.value = '';
        fetchCatalogItems();
      }
    });
  });
}

/**
 * Toggle selection of a catalog item
 */
export function toggleCatalogSelection(itemId: string, isSelected: boolean): void {
  if (isSelected) {
    state.batch.catalog.selectedItemIds.add(itemId);
  } else {
    state.batch.catalog.selectedItemIds.delete(itemId);
  }
  updateSelectionBar();
}

/**
 * Select / Deselect all catalog items on current page
 */
export function selectAllCatalogItems(isSelected: boolean): void {
  state.batch.catalog.items.forEach((item) => {
    const id = item.identifier || item.code;
    if (id) {
      if (isSelected) {
        state.batch.catalog.selectedItemIds.add(id);
      } else {
        state.batch.catalog.selectedItemIds.delete(id);
      }
    }
  });
  renderCatalogTable();
}

/**
 * Clear all selected items
 */
export function clearCatalogSelection(): void {
  state.batch.catalog.selectedItemIds.clear();
  renderCatalogTable();
}

/**
 * Update selection bar visibility and badges
 */
export function updateSelectionBar(): void {
  const bar = document.getElementById('catalog-selection-bar');
  const countEl = document.getElementById('catalog-selected-count');
  const badgeSelected = document.getElementById('badge-batch-selected-count');
  const metricItems = document.getElementById('metric-batch-items-count');

  const count = state.batch.catalog.selectedItemIds.size;

  if (countEl) countEl.textContent = count.toLocaleString();
  if (badgeSelected) badgeSelected.textContent = `${count} items`;
  if (metricItems) metricItems.textContent = `${count} specific items checked`;

  if (bar) {
    bar.style.display = count > 0 ? 'flex' : 'none';
  }
}

/**
 * Execute Selective Sync on chosen items
 */
export function executeSelectiveSync(): void {
  const selected = Array.from(state.batch.catalog.selectedItemIds);
  if (selected.length === 0) {
    showToast('Please select at least one catalog item to sync', 'warn');
    return;
  }

  executeBatchSyncJob(false, {
    items: selected,
    entity_types: [state.batch.catalog.entityType],
    cascade: true,
  });
}

/**
 * Execute Selective Dry-Run on chosen items
 */
export function executeSelectiveDryRun(): void {
  const selected = Array.from(state.batch.catalog.selectedItemIds);
  if (selected.length === 0) {
    showToast('Please select at least one catalog item for dry-run', 'warn');
    return;
  }

  executeBatchSyncJob(true, {
    items: selected,
    entity_types: [state.batch.catalog.entityType],
    cascade: true,
  });
}

/**
 * Inspect single catalog entity in modal
 */
export async function inspectCatalogEntity(entityType: string, entityId: string): Promise<void> {
  const modal = document.getElementById('entity-inspect-modal');
  const title = document.getElementById('entity-inspect-title');
  const subtitle = document.getElementById('entity-inspect-subtitle');
  const tagsContainer = document.getElementById('entity-inspect-tags');
  const codeEl = document.getElementById('entity-inspect-json-code');

  if (!modal) return;

  state.batch.inspectEntity = { entityType, entityId };

  if (title) title.textContent = `Inspecting ${entityType === 'products' ? 'Product' : 'Model'}: ${entityId}`;
  if (subtitle) subtitle.textContent = `Raw Source Instance Payload & Properties`;
  if (tagsContainer) {
    tagsContainer.innerHTML = `
      <span class="badge badge-info">${entityType}</span>
      <span class="dep-pill">${entityId}</span>
    `;
  }
  if (codeEl) codeEl.innerHTML = '<code>Loading entity details from source...</code>';

  modal.style.display = 'flex';

  try {
    const endpoint = entityType === 'products'
      ? `/api/v1/discovery/products/${encodeURIComponent(entityId)}`
      : `/api/v1/discovery/product-models/${encodeURIComponent(entityId)}`;
    const res = await fetch(endpoint);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    const payload = data.item || data.product || data.product_model || data;

    state.batch.inspectEntity.payload = payload;

    if (codeEl) {
      codeEl.innerHTML = `<code>${escapeHtml(JSON.stringify(payload, null, 2))}</code>`;
    }
  } catch (err: any) {
    if (codeEl) {
      codeEl.innerHTML = `<code class="text-danger">Failed to fetch entity details: ${escapeHtml(err.message)}</code>`;
    }
  }
}

/**
 * Render Entity Types Grid in Orchestrator View
 */
export function renderEntityTypesGrid(): void {
  const grid = document.getElementById('entity-types-grid');
  if (!grid) return;

  const selected = new Set(state.batch.orchestrator.selectedEntityTypes);

  const html = Object.entries(ENTITY_TYPE_METADATA).map(([typeKey, meta]) => {
    const isChecked = selected.has(typeKey);
    return `
      <div class="entity-type-card ${isChecked ? 'selected' : ''}" data-type="${escapeHtml(typeKey)}">
        <input type="checkbox" id="cb-entity-${escapeHtml(typeKey)}" class="form-checkbox entity-type-cb" data-type="${escapeHtml(typeKey)}" ${isChecked ? 'checked' : ''} />
        <div class="entity-card-icon">${meta.icon}</div>
        <div class="entity-card-info">
          <div class="entity-card-name">
            <span>${escapeHtml(meta.name)}</span>
          </div>
          <p class="entity-card-desc">${escapeHtml(meta.desc)}</p>
          ${
            meta.deps.length > 0
              ? `<div class="entity-deps-row">
                  <span>Prereqs:</span>
                  ${meta.deps.map((d) => `<span class="dep-pill">${escapeHtml(d)}</span>`).join('')}
                </div>`
              : ''
          }
        </div>
      </div>
    `;
  }).join('');

  grid.innerHTML = html;

  // Update Metric
  const metricSelected = document.getElementById('metric-batch-selected');
  if (metricSelected) metricSelected.textContent = String(selected.size);

  // Event handlers
  grid.querySelectorAll('.entity-type-card').forEach((card) => {
    card.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).classList.contains('entity-type-cb')) return;
      const cb = card.querySelector('.entity-type-cb') as HTMLInputElement | null;
      if (cb) {
        cb.checked = !cb.checked;
        toggleEntityTypeSelection(cb.getAttribute('data-type')!, cb.checked);
      }
    });
  });

  grid.querySelectorAll('.entity-type-cb').forEach((cb) => {
    cb.addEventListener('change', () => {
      toggleEntityTypeSelection(cb.getAttribute('data-type')!, (cb as HTMLInputElement).checked);
    });
  });
}

/**
 * Toggle Entity Type selection
 */
export function toggleEntityTypeSelection(entityType: string, isSelected: boolean): void {
  const current = new Set(state.batch.orchestrator.selectedEntityTypes);
  if (isSelected) {
    current.add(entityType);
  } else {
    current.delete(entityType);
  }
  state.batch.orchestrator.selectedEntityTypes = Array.from(current);
  renderEntityTypesGrid();
  updateTopologicalOrder();
}

/**
 * Preset selection for Entity Types
 */
export function setEntityTypePreset(presetName: 'all' | 'catalog' | 'structure' | 'config' | 'none'): void {
  if (presetName === 'all') {
    state.batch.orchestrator.selectedEntityTypes = Object.keys(ENTITY_TYPE_METADATA);
  } else if (presetName === 'catalog') {
    state.batch.orchestrator.selectedEntityTypes = ['categories', 'families', 'family_variants', 'product_models', 'products'];
  } else if (presetName === 'structure') {
    state.batch.orchestrator.selectedEntityTypes = ['categories', 'attribute_groups', 'attributes', 'attribute_options', 'families', 'family_variants'];
  } else if (presetName === 'config') {
    state.batch.orchestrator.selectedEntityTypes = ['system_config', 'locales', 'currencies', 'association_types'];
  } else if (presetName === 'none') {
    state.batch.orchestrator.selectedEntityTypes = [];
  }

  renderEntityTypesGrid();
  updateTopologicalOrder();
  showToast(`Applied preset: ${presetName}`, 'info', 1200);
}

/**
 * Compute and render topological DAG execution sequence
 */
export function updateTopologicalOrder(): void {
  const container = document.getElementById('topological-flow-container');
  if (!container) return;

  const fullSequence = [
    'system_config',
    'locales',
    'currencies',
    'association_types',
    'categories',
    'attribute_groups',
    'attributes',
    'attribute_options',
    'families',
    'family_variants',
    'product_models',
    'products',
    'asset_families',
    'assets',
  ];

  const selected = new Set(state.batch.orchestrator.selectedEntityTypes);
  const topoList = fullSequence.filter((t) => selected.has(t));
  state.batch.orchestrator.topologicalOrder = topoList;

  if (topoList.length === 0) {
    container.innerHTML = '<div style="color: var(--text-muted); font-size: 0.85rem; padding: 0.5rem;">No entity types selected for execution pipeline.</div>';
    return;
  }

  const nodesHtml = topoList.map((typeKey, idx) => {
    const meta = ENTITY_TYPE_METADATA[typeKey] || { name: typeKey, icon: '📦' };
    const isLast = idx === topoList.length - 1;
    return `
      <div class="topo-node-card">
        <span class="topo-node-index">${idx + 1}</span>
        <span>${meta.icon}</span>
        <span class="topo-node-name">${escapeHtml(meta.name)}</span>
      </div>
      ${!isLast ? '<div class="topo-arrow">➔</div>' : ''}
    `;
  }).join('');

  container.innerHTML = nodesHtml;
}

/**
 * Check target instance readiness for sync
 */
export async function checkBatchReadiness(): Promise<void> {
  const badgeMetric = document.getElementById('metric-batch-readiness-badge');
  const textMetric = document.getElementById('metric-batch-readiness');
  const valBadge = document.getElementById('val-readiness-status');

  try {
    const res = await fetch('/api/v1/sync/readiness');
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    const isReady = data.readiness?.status === 'ready' || data.success === true;

    if (badgeMetric) {
      badgeMetric.className = `badge ${isReady ? 'badge-success' : 'badge-danger'}`;
      badgeMetric.textContent = isReady ? 'Ready' : 'Not Ready';
    }
    if (textMetric) {
      textMetric.className = `metric-number ${isReady ? 'text-success' : 'text-danger'}`;
      textMetric.textContent = isReady ? 'Ready' : 'Failed';
    }
    if (valBadge) {
      valBadge.className = `badge ${isReady ? 'badge-success' : 'badge-danger'}`;
      valBadge.textContent = `Target: ${isReady ? 'Ready' : 'Not Ready'}`;
    }
  } catch {
    if (badgeMetric) {
      badgeMetric.className = 'badge badge-warning';
      badgeMetric.textContent = 'Warning';
    }
    if (textMetric) {
      textMetric.className = 'metric-number text-warning';
      textMetric.textContent = 'Check Link';
    }
  }
}

export function getPruneOptions(): { prune: boolean; max_delete_percentage: number | undefined; force_delete: boolean } {
  const pruneCb = document.getElementById('opt-prune-deleted') as HTMLInputElement | null;
  const forceDeleteCb = document.getElementById('opt-force-delete') as HTMLInputElement | null;
  const thresholdInput = document.getElementById('opt-prune-threshold') as HTMLInputElement | null;
  const pruneEnabled = pruneCb?.checked ?? state.batch.orchestrator.pruneEnabled;
  const threshold = thresholdInput?.value ? Number(thresholdInput.value) : (state.batch.orchestrator.pruneThreshold ?? 15);
  return {
    prune: pruneEnabled,
    max_delete_percentage: pruneEnabled ? threshold : undefined,
    force_delete: forceDeleteCb?.checked ?? state.batch.orchestrator.forceDelete,
  };
}

export function setPruneEnabled(enabled: boolean): void {
  state.batch.orchestrator.pruneEnabled = enabled;
  const cb = document.getElementById('opt-prune-deleted') as HTMLInputElement | null;
  if (cb) cb.checked = enabled;
}

export function setPruneThreshold(threshold: number | null): void {
  state.batch.orchestrator.pruneThreshold = threshold;
  const input = document.getElementById('opt-prune-threshold') as HTMLInputElement | null;
  if (input) input.value = threshold !== null && threshold !== undefined ? String(threshold) : '15';
}

export function setForceDelete(force: boolean): void {
  state.batch.orchestrator.forceDelete = force;
  const cb = document.getElementById('opt-force-delete') as HTMLInputElement | null;
  if (cb) cb.checked = force;
}

/**
 * Execute pre-flight validation scan
 */
export async function runPreFlightValidation(): Promise<void> {
  const btn = document.getElementById('btn-run-validation') || document.getElementById('btn-execute-validate');
  if (btn) btn.classList.add('loading');

  showToast('Starting pre-flight validation scan...', 'info', 2000);

  const payload = {
    entity_types: state.batch.orchestrator.selectedEntityTypes,
    items: state.batch.catalog.selectedItemIds.size > 0 ? Array.from(state.batch.catalog.selectedItemIds) : undefined,
    strict_mode: (document.getElementById('opt-strict-mode') as HTMLInputElement | null)?.checked || false,
    validate_dependencies: (document.getElementById('opt-validate-deps') as HTMLInputElement | null)?.checked ?? true,
    check_readiness: (document.getElementById('opt-check-readiness') as HTMLInputElement | null)?.checked ?? true,
    include_diffs: (document.getElementById('opt-include-diffs') as HTMLInputElement | null)?.checked ?? true,
    force: (document.getElementById('opt-force-sync') as HTMLInputElement | null)?.checked || false,
    limit: (document.getElementById('opt-limit-count') as HTMLInputElement | null)?.value ? Number((document.getElementById('opt-limit-count') as HTMLInputElement).value) : undefined,
    ...getPruneOptions(),
  };

  try {
    const res = await fetch('/api/v1/sync/validate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    state.batch.validation.scanResult = data.validation;

    renderValidationSummary(data.validation);
    if (data.validation.diffPlan) {
      renderDiffInspector(data.validation.diffPlan);
    }

    switchBatchSubTab('diffs');
    showToast('Pre-flight validation completed successfully', 'success');
  } catch (err: any) {
    showToast(`Validation scan failed: ${err.message}`, 'error');
  } finally {
    if (btn) btn.classList.remove('loading');
  }
}

/**
 * Preview Dry-Run and Diff Plan
 */
export async function runDryRunDiffPreview(): Promise<void> {
  const btn = document.getElementById('btn-run-dry-run') || document.getElementById('btn-execute-dryrun');
  if (btn) btn.classList.add('loading');

  showToast('Calculating dry-run diff plan...', 'info', 2000);

  const payload = {
    entity_types: state.batch.orchestrator.selectedEntityTypes,
    items: state.batch.catalog.selectedItemIds.size > 0 ? Array.from(state.batch.catalog.selectedItemIds) : undefined,
    include_diffs: true,
    force: (document.getElementById('opt-force-sync') as HTMLInputElement | null)?.checked || false,
    limit: (document.getElementById('opt-limit-count') as HTMLInputElement | null)?.value ? Number((document.getElementById('opt-limit-count') as HTMLInputElement).value) : undefined,
    ...getPruneOptions(),
  };

  try {
    const res = await fetch('/api/v1/sync/diff-plan', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    const plan: DiffPlan = data.plan;

    state.batch.validation.diffPlan = plan;

    renderValidationSummary({
      targetReadiness: { status: 'ready' },
      dependencyValidation: { valid: true },
      summary: {
        toCreate: plan.toCreate || 0,
        toUpdate: plan.toUpdate || 0,
        unchanged: plan.unchanged || 0,
        missingDependencies: 0,
      },
    });

    renderDiffInspector(plan);
    switchBatchSubTab('diffs');
    showToast(`Dry-run diffs preview generated: ${plan.toCreate + plan.toUpdate} changes`, 'success');
  } catch (err: any) {
    showToast(`Dry-run simulation failed: ${err.message}`, 'error');
  } finally {
    if (btn) btn.classList.remove('loading');
  }
}

/**
 * Render Validation Summary Matrix
 */
export function renderValidationSummary(val: ValidationScanResult): void {
  if (!val) return;

  const targetBadge = document.getElementById('val-readiness-status');
  const depsBadge = document.getElementById('val-deps-status');
  const countCreated = document.getElementById('val-count-created');
  const countUpdated = document.getElementById('val-count-updated');
  const countUnchanged = document.getElementById('val-count-unchanged');
  const countErrors = document.getElementById('val-count-errors');
  const metricDiffs = document.getElementById('metric-batch-diffs');
  const badgeDiffStatus = document.getElementById('badge-batch-diff-status');
  const diffsCountBadge = document.getElementById('batch-diff-count-badge');

  const summary = val.summary || {};
  const toCreate = summary.toCreate || 0;
  const toUpdate = summary.toUpdate || 0;
  const unchanged = summary.unchanged || 0;
  const errors = (summary.missingDependencies || 0) + (val.errors?.length || 0);

  if (countCreated) countCreated.textContent = toCreate.toLocaleString();
  if (countUpdated) countUpdated.textContent = toUpdate.toLocaleString();
  if (countUnchanged) countUnchanged.textContent = unchanged.toLocaleString();
  if (countErrors) countErrors.textContent = errors.toLocaleString();

  const totalChanges = toCreate + toUpdate;
  if (metricDiffs) metricDiffs.textContent = totalChanges.toLocaleString();
  if (badgeDiffStatus) {
    badgeDiffStatus.className = `badge ${totalChanges > 0 ? 'badge-info' : 'badge-neutral'}`;
    badgeDiffStatus.textContent = totalChanges > 0 ? `${totalChanges} Diffs` : 'Clean';
  }
  if (diffsCountBadge) {
    diffsCountBadge.style.display = totalChanges > 0 ? 'inline-block' : 'none';
    diffsCountBadge.textContent = totalChanges.toLocaleString();
  }

  if (targetBadge && val.targetReadiness) {
    const ready = val.targetReadiness.status === 'ready';
    targetBadge.className = `badge ${ready ? 'badge-success' : 'badge-danger'}`;
    targetBadge.textContent = `Target: ${ready ? 'Ready' : 'Not Ready'}`;
  }

  if (depsBadge && val.dependencyValidation) {
    const valid = val.dependencyValidation.valid !== false;
    depsBadge.className = `badge ${valid ? 'badge-success' : 'badge-warning'}`;
    depsBadge.textContent = `Dependencies: ${valid ? 'Valid DAG' : 'Issues Found'}`;
  }
}

/**
 * Render Interactive Diff Inspector
 */
export function renderDiffInspector(diffPlan: DiffPlan): void {
  const container = document.getElementById('diff-items-container');
  const filterSelect = document.getElementById('diff-filter-entity') as HTMLSelectElement | null;
  if (!container) return;

  if (!diffPlan || !diffPlan.stages) {
    container.innerHTML = `
      <div class="card empty-diff-card" id="diff-empty-placeholder">
        <div class="placeholder-icon">🔍</div>
        <h4>No Dry-Run Diff Generated Yet</h4>
        <p>Run a Dry-Run simulation or Pre-Flight Validation Scan to inspect field-level diffs and payload comparisons.</p>
        <button type="button" class="btn btn-secondary btn-sm" id="btn-trigger-diff-from-empty">Run Dry-Run Now</button>
      </div>
    `;
    const btnFromEmpty = document.getElementById('btn-trigger-diff-from-empty');
    if (btnFromEmpty) btnFromEmpty.addEventListener('click', runDryRunDiffPreview);
    return;
  }

  // Populate entity types filter
  if (filterSelect) {
    const entityTypes = Object.keys(diffPlan.stages);
    filterSelect.innerHTML = '<option value="all">All Entity Types</option>' +
      entityTypes.map((t) => `<option value="${escapeHtml(t)}">${escapeHtml(t)} (${diffPlan.stages![t].items?.length || 0})</option>`).join('');
  }

  // Flatten all diff items from all stages
  const allItems: any[] = [];
  Object.entries(diffPlan.stages).forEach(([typeKey, stage]) => {
    if (Array.isArray(stage.items)) {
      stage.items.forEach((item) => {
        allItems.push({ ...item, entityType: item.entityType || typeKey });
      });
    }
  });

  state.batch.validation.diffItems = allItems;
  filterDiffItems();
}

/**
 * Filter diff cards
 */
export function filterDiffItems(): void {
  const container = document.getElementById('diff-items-container');
  if (!container) return;

  const items = state.batch.validation.diffItems || [];
  const entityFilter = (document.getElementById('diff-filter-entity') as HTMLSelectElement | null)?.value || 'all';
  const actionFilter = (document.getElementById('diff-filter-action') as HTMLSelectElement | null)?.value || 'all';
  const search = ((document.getElementById('diff-search-input') as HTMLInputElement | null)?.value || '').trim().toLowerCase();

  const filtered = items.filter((item) => {
    if (entityFilter !== 'all' && item.entityType !== entityFilter) return false;
    if (actionFilter !== 'all') {
      const action = item.action || 'unchanged';
      if (actionFilter === 'error' && !item.missingDependencies?.length) return false;
      if (actionFilter !== 'error' && action !== actionFilter) return false;
    }
    if (search) {
      const idMatch = (item.id || '').toLowerCase().includes(search);
      const typeMatch = (item.entityType || '').toLowerCase().includes(search);
      if (!idMatch && !typeMatch) return false;
    }
    return true;
  });

  if (filtered.length === 0) {
    container.innerHTML = `
      <div class="card empty-diff-card">
        <div class="placeholder-icon">✓</div>
        <h4>No Matching Diffs Found</h4>
        <p>No entity differences match the selected filters.</p>
      </div>
    `;
    return;
  }

  const html = filtered.map((item) => {
    const action = item.action || (item.diff?.hasChanges ? 'updated' : 'unchanged');
    const isExpanded = state.batch.validation.expandedDiffIds.has(`${item.entityType}:${item.id}`);
    const changes = item.diff?.changes || [];
    const sourceHash = (item.sourceHash || item.diff?.sourceHash || '').substring(0, 16);
    const targetHash = (item.targetHash || item.diff?.targetHash || 'none').substring(0, 16);

    let actionBadgeClass = 'badge-neutral';
    let actionLabel = 'Unchanged';
    if (action === 'created') {
      actionBadgeClass = 'badge-success';
      actionLabel = 'Created (New)';
    } else if (action === 'updated') {
      actionBadgeClass = 'badge-info';
      actionLabel = 'Updated (Modified)';
    } else if (item.missingDependencies?.length) {
      actionBadgeClass = 'badge-danger';
      actionLabel = 'Missing Dependencies';
    }

    return `
      <div class="diff-item-card" data-key="${escapeHtml(item.entityType)}:${escapeHtml(item.id)}">
        <div class="diff-item-header" data-toggle-diff="${escapeHtml(item.entityType)}:${escapeHtml(item.id)}">
          <div class="diff-header-left">
            <span class="badge badge-neutral">${escapeHtml(item.entityType)}</span>
            <strong class="mono">${escapeHtml(item.id)}</strong>
            <span class="badge ${actionBadgeClass}">${actionLabel}</span>
          </div>
          <div class="diff-header-right">
            <span class="text-secondary" style="font-size: 0.775rem;">${changes.length} field changes</span>
            <button type="button" class="tree-expander-btn ${isExpanded ? 'expanded' : ''}">▶</button>
          </div>
        </div>
        <div class="diff-item-body" style="display: ${isExpanded ? 'block' : 'none'};">
          <div class="diff-hashes-row">
            <span><strong>Source Hash:</strong> <code class="mono">${escapeHtml(sourceHash)}...</code></span>
            <span><strong>Target Hash:</strong> <code class="mono">${escapeHtml(targetHash)}...</code></span>
          </div>
          ${
            item.missingDependencies?.length
              ? `<div class="alert-box-warning" style="margin-bottom: 0.75rem;">
                  <strong>Missing Dependencies:</strong> ${item.missingDependencies.map((d: any) => `${escapeHtml(d.type)}:${escapeHtml(d.id)}`).join(', ')}
                </div>`
              : ''
          }
          ${
            changes.length > 0
              ? `<table class="diff-field-table">
                  <thead>
                    <tr>
                      <th style="width: 30%;">Field / Attribute</th>
                      <th style="width: 35%;">Target Value (Before)</th>
                      <th style="width: 35%;">Source Value (After)</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${changes.map((c: any) => `
                      <tr>
                        <td class="mono"><strong>${escapeHtml(c.path)}</strong></td>
                        <td><span class="diff-old-val">${escapeHtml(JSON.stringify(c.oldValue ?? null))}</span></td>
                        <td><span class="diff-new-val">${escapeHtml(JSON.stringify(c.newValue ?? null))}</span></td>
                      </tr>
                    `).join('')}
                  </tbody>
                </table>`
              : '<div class="text-secondary" style="font-size: 0.8rem;">No field-level attribute modifications detected (exact payload match).</div>'
          }
        </div>
      </div>
    `;
  }).join('');

  container.innerHTML = html;

  container.querySelectorAll('[data-toggle-diff]').forEach((header) => {
    header.addEventListener('click', () => {
      const key = header.getAttribute('data-toggle-diff');
      if (!key) return;
      if (state.batch.validation.expandedDiffIds.has(key)) {
        state.batch.validation.expandedDiffIds.delete(key);
      } else {
        state.batch.validation.expandedDiffIds.add(key);
      }
      filterDiffItems();
    });
  });
}

/**
 * Launch Batch Sync or Dry-Run job
 */
export async function executeBatchSyncJob(isDryRun: boolean = false, customOptions: Record<string, any> = {}): Promise<void> {
  const isSelective = Array.isArray(customOptions.items) && customOptions.items.length > 0;
  const entityTypes = customOptions.entity_types || state.batch.orchestrator.selectedEntityTypes;

  if (!isSelective && (!entityTypes || entityTypes.length === 0)) {
    showToast('Please select at least one entity type to execute batch sync', 'warn');
    return;
  }

  const payload = {
    entity_types: entityTypes,
    items: customOptions.items,
    entity_options: customOptions.entity_options,
    source_connection_id: customOptions.source_connection_id,
    target_connection_id: customOptions.target_connection_id,
    cascade: customOptions.cascade ?? (isSelective ? true : false),
    strategy: customOptions.strategy || 'lean',
    dry_run: isDryRun,
    strict_mode: (document.getElementById('opt-strict-mode') as HTMLInputElement | null)?.checked || false,
    validate_dependencies: (document.getElementById('opt-validate-deps') as HTMLInputElement | null)?.checked ?? true,
    check_readiness: (document.getElementById('opt-check-readiness') as HTMLInputElement | null)?.checked ?? true,
    include_diffs: (document.getElementById('opt-include-diffs') as HTMLInputElement | null)?.checked ?? true,
    force: (document.getElementById('opt-force-sync') as HTMLInputElement | null)?.checked || false,
    stop_on_error: (document.getElementById('opt-stop-on-error') as HTMLInputElement | null)?.checked || false,
    limit: (document.getElementById('opt-limit-count') as HTMLInputElement | null)?.value ? Number((document.getElementById('opt-limit-count') as HTMLInputElement).value) : undefined,
    async: true,
    ...getPruneOptions(),
  };

  const endpoint = isDryRun ? '/api/v1/sync/dry-run' : '/api/v1/sync/batch';

  try {
    showToast(`Launching ${isDryRun ? 'Dry-Run Simulation' : 'Batch Migration Job'}...`, 'info', 2000);
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    const jobId = data.job_id || data.job?.id;

    if (jobId) {
      openBatchProgressModal(jobId, isDryRun);
      showToast(`Job ${jobId} started`, 'success');
    }
  } catch (err: any) {
    showToast(`Failed to launch batch job: ${err.message}`, 'error');
  }
}

/**
 * Open batch execution progress modal and start live polling
 */
export function openBatchProgressModal(jobId: string, isDryRun: boolean): void {
  const modal = document.getElementById('batch-progress-modal');
  const title = document.getElementById('batch-progress-title');
  const subtitle = document.getElementById('batch-progress-subtitle');
  const icon = document.getElementById('batch-modal-icon');
  const bar = document.getElementById('batch-modal-progress-bar');
  const percent = document.getElementById('batch-modal-progress-percent');
  const stage = document.getElementById('batch-modal-stage-label');
  const counts = document.getElementById('batch-modal-counts-text');
  const logsList = document.getElementById('batch-modal-logs-list');

  if (!modal) return;

  state.batch.activeJobId = jobId;

  if (title) title.textContent = `${isDryRun ? 'Dry-Run Simulation' : 'Batch Migration'}: ${jobId}`;
  if (subtitle) subtitle.textContent = `Status: Running • Pipeline executing topological DAG stages`;
  if (icon) icon.textContent = isDryRun ? '🔍' : '⚡';
  if (bar) bar.style.width = '5%';
  if (percent) percent.textContent = '5%';
  if (stage) stage.textContent = 'Starting DAG orchestrator...';
  if (counts) counts.textContent = '0 entities processed';
  if (logsList) {
    logsList.innerHTML = `
      <div class="audit-log-item log-info">
        <span class="log-time">[${new Date().toLocaleTimeString()}]</span>
        <span class="log-message">Job ${escapeHtml(jobId)} queued for background execution...</span>
      </div>
    `;
  }

  modal.style.display = 'flex';

  if (state.batch.progressPollTimer) {
    clearInterval(state.batch.progressPollTimer);
  }

  const pollJob = async () => {
    if (!state.batch.activeJobId) {
      if (state.batch.progressPollTimer) clearInterval(state.batch.progressPollTimer);
      return;
    }

    try {
      const res = await fetch(`/api/v1/jobs/${encodeURIComponent(state.batch.activeJobId)}`);
      if (!res.ok) return;
      const data = await res.json();
      const job = data.job;
      if (job) {
        updateBatchProgressModal(job);
        if (job.status === 'completed' || job.status === 'failed' || job.status === 'cancelled') {
          if (state.batch.progressPollTimer) {
            clearInterval(state.batch.progressPollTimer);
            state.batch.progressPollTimer = null;
          }
        }
      }
    } catch (err) {
      console.warn('Error polling job progress:', err);
    }
  };

  pollJob();
  state.batch.progressPollTimer = setInterval(pollJob, 800);
}

/**
 * Update Progress Modal with live job updates
 */
export function updateBatchProgressModal(job: any): void {
  const bar = document.getElementById('batch-modal-progress-bar');
  const percentEl = document.getElementById('batch-modal-progress-percent');
  const stageEl = document.getElementById('batch-modal-stage-label');
  const countsEl = document.getElementById('batch-modal-counts-text');
  const createdEl = document.getElementById('batch-modal-created');
  const updatedEl = document.getElementById('batch-modal-updated');
  const unchangedEl = document.getElementById('batch-modal-unchanged');
  const failedEl = document.getElementById('batch-modal-failed');
  const subtitleEl = document.getElementById('batch-progress-subtitle');

  const total = job.total_entities || job.progress?.totalEntities || 1;
  const processed = job.processed_entities || job.progress?.processedEntities || (job.status === 'completed' ? total : 0);
  const percent = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : (job.status === 'completed' ? 100 : 10);

  if (bar) bar.style.width = `${percent}%`;
  if (percentEl) percentEl.textContent = `${percent}%`;
  if (countsEl) countsEl.textContent = `${processed} / ${total} entities processed`;

  if (stageEl) {
    if (job.status === 'completed') stageEl.textContent = 'All DAG pipeline stages completed successfully.';
    else if (job.status === 'failed') stageEl.textContent = `Job failed: ${job.error || 'Execution error'}`;
    else stageEl.textContent = job.progress?.currentStage ? `Processing Stage: ${job.progress.currentStage}` : 'Executing sync stages...';
  }

  if (createdEl) createdEl.textContent = (job.created_count || job.progress?.createdCount || 0).toLocaleString();
  if (updatedEl) updatedEl.textContent = (job.updated_count || job.progress?.updatedCount || 0).toLocaleString();
  if (unchangedEl) unchangedEl.textContent = (job.skipped_count || job.progress?.skippedCount || 0).toLocaleString();
  if (failedEl) failedEl.textContent = (job.failed_count || job.progress?.failedCount || 0).toLocaleString();

  if (subtitleEl) {
    subtitleEl.textContent = `Status: ${job.status.toUpperCase()} • ${percent}% complete`;
  }

  // Show direct failed items banner and link to Job Details Modal
  const failedAlert = document.getElementById('batch-modal-failed-alert');
  const failedTitle = document.getElementById('batch-modal-failed-title');
  const failedSubtitle = document.getElementById('batch-modal-failed-subtitle');
  const btnViewFailures = document.getElementById('btn-batch-modal-view-failures');

  const failedCount = job.failed_count || job.progress?.failedCount || 0;
  if (failedAlert) {
    if (failedCount > 0) {
      failedAlert.style.display = 'flex';
      if (failedTitle) failedTitle.textContent = `⚠️ ${failedCount} Failed Items Detected`;
      if (failedSubtitle) failedSubtitle.textContent = `Target Akeneo instance rejected ${failedCount} items during sync. Click to view exact error messages.`;
      if (btnViewFailures) {
        btnViewFailures.onclick = () => {
          const progressModal = document.getElementById('batch-progress-modal');
          if (progressModal) progressModal.style.display = 'none';
          openJobDetailModal(job.id || state.batch.activeJobId || '');
        };
      }
    } else {
      failedAlert.style.display = 'none';
    }
  }
}
